import React from 'react';
import {
  makeStyles, Popover, useTheme, Typography, Card, CardContent,
} from '@material-ui/core';
import {
  LineChart, Line, CartesianGrid, YAxis, XAxis, Tooltip, TooltipProps, Label,
} from 'recharts';
import moment from 'moment';

import { PatientData } from '../generated-api';
import { getFunctionPromisScore } from '../utils/survey';
import PromisScore from './PromisScore';
import PromisFunctionScale from '../assets/PromisFunctionScale';
import PromisPainScale from '../assets/PromisPainScale';
import PromisScalePoint from './PromisScalePoint';

export interface PromisHistoryPopoverProps {
  id?: string;
  open: boolean;
  anchorEl: Element | null;
  onClose: () => any;
  patient: PatientData;
  type: 'pain' | 'function';
}

interface ChartDataPoint {
  date: Date;
  dateStr: string;
  score: number;
}

export default function PromisHistoryPopover(props: PromisHistoryPopoverProps) {
  const {
    id, open, anchorEl, onClose, patient, type,
  } = props;
  const { surveys } = patient;

  const classes = useStyles();
  const theme = useTheme();

  const chartData: Array<ChartDataPoint> = surveys.map((survey) => ({
    date: survey.date,
    dateStr: moment(survey.date).format('M/D/YYYY'),
    score: type === 'pain'
      ? survey.promisScores.painInterference
      : getFunctionPromisScore(survey, patient.program),
  }));

  const minScore = Math.min(...chartData.map((data) => data.score));
  const maxScore = Math.max(...chartData.map((data) => data.score));
  const domain: [number, number] = [
    Math.max((Math.floor(minScore / 5) - 1) * 5, 0),
    Math.min((Math.ceil(maxScore / 5) + 1) * 5, 100),
  ];
  const ticks: Array<number> = [];
  for (let tick = domain[0]; tick <= domain[1]; tick += 5) {
    ticks.push(tick);
  }

  const realChartWidth = 80 * (surveys.length + 1);
  const chartWidth = Math.max(realChartWidth, 400);
  const extraRightPadding = chartWidth - realChartWidth;

  const PromisTooltip = ({ active, payload, label }: TooltipProps) => {
    if (!active || payload == null) return null;

    const dataPoint = payload[0].payload! as ChartDataPoint;

    return (
      <Card className={classes.tooltip}>
        <CardContent>
          <Typography component="p">{dataPoint.dateStr}</Typography>
          <Typography component="p">
            <PromisScore
              score={dataPoint.score}
              type={type}
              showCategory
              classes={{ category: classes.promisCategory }}
            />
          </Typography>
        </CardContent>
      </Card>
    );
  };

  const PromisScale = type === 'pain' ? PromisPainScale : PromisFunctionScale;

  return (
    <Popover
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      classes={{ paper: classes.popover }}
    >
      <div style={{ width: chartWidth }}>
        <LineChart
          width={chartWidth}
          height={300}
          data={chartData}
          className={classes.chart}
          margin={{
            top: 1, right: 1, bottom: 0, left: 0,
          }}
        >
          <CartesianGrid stroke="#ccc" />
          <XAxis
            dataKey="dateStr"
            label="Date"
            height={80}
            padding={{ left: 30, right: 30 + extraRightPadding }}
          />
          <YAxis
            domain={domain}
            ticks={ticks}
            interval={0}
          >
            <Label
              value={`PROMIS® ${type === 'pain' ? 'Pain' : 'Function'} Score`}
              angle={-90}
              position='insideLeft'
              style={{ textAnchor: 'middle' }}
            />
          </YAxis>
          <Line
            type="linear"
            dataKey="score"
            stroke={theme.palette.primary.main}
            strokeWidth={2}
            dot={{
              fill: theme.palette.primary.main,
              r: 4,
            }}
            activeDot={{
              fill: theme.palette.primary.main,
              r: 6,
              strokeWidth: 0,
            }}
            isAnimationActive={false}
          />
          <Tooltip
            // @ts-ignore
            content={<PromisTooltip />}
            isAnimationActive={false}
          />
        </LineChart>
        <Typography component="p" className={classes.explanation}>
          The minimal clinical important difference (MCID value) is a
          {type === 'pain'
            ? ' 2 point reduction in pain '
            : ' 2.4 point increase in function '}
          from baseline.
        </Typography>
        <div className={classes.promisScaleContainer}>
          <Typography component="h6">
            PROMIS® {type === 'pain' ? 'Pain' : 'Function'} Scale
          </Typography>
          <PromisScale className={classes.promisScale}>
            {chartData.map((datum) => (
              <PromisScalePoint score={datum.score} />
            ))}
          </PromisScale>
        </div>
      </div>
    </Popover>
  );
}

const useStyles = makeStyles((theme) => ({
  popover: {
    padding: theme.spacing(3),
  },
  chart: {
    marginBottom: -30,
    '& svg': {
      overflow: 'visible',
    },
    '& text': {
      fontFamily: theme.typography.body1.fontFamily,
      fontSize: 14,
    },
    '& .recharts-label': {
      fontWeight: 'bold',
    },
  },
  tooltip: {
    '& p': {
      fontSize: 14,
    },
    '& .MuiCardContent-root': {
      padding: theme.spacing(1),
    },
  },
  promisCategory: {
    display: 'block',
  },
  explanation: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    fontSize: 14,
  },
  promisScaleContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    '& > h6': {
      fontWeight: 'bold',
      fontSize: 14,
    },
  },
  promisScale: {
    width: 400,
    marginTop: theme.spacing(1),
  },
}));
